import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import CustomerService from "../../api/CustomerService";
import UserService from "../../api/UserService";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import { Kunden } from "../../components/Kunden/Kunden";
import { LoadingSpinner } from "../../components/LoadingSpinner/LoadingSpinner";
import Modal from "../../components/Modal/Modal";
import { SubMenuButton } from "../../components/SubMenuButton/SubMenuButton";
import UserListComponent from "../../components/UserListComponent/UserListComponent";
import UsersCard from "../../components/UserListComponent/UsersCard";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useScreenSize } from "../../hooks/useScreenSize";
import { AddUserIcon } from "../../icons/AddUser/AddUserIcon";
import { LeftArrowIcon } from "../../icons/LeftArrowIcon/LeftArrowIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { RootState } from "../../store";
import { activeTabsActions } from "../../store/tabs-slice";
import { userManagementActions } from "../../store/user-management-slice";
import { ROLES } from "../../store/user-slice";
import styles from "../../styles/user-management.module.css";
import { Technician, User, UsersQueryParams } from "../../types/user";

export const SUB_MENU_BUTTONS: string[] = ["Schädlingsbekämpfung", "Kunden"];

export const UserManagement = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );
  const [currentTab, setCurrentTab] = useState(SUB_MENU_BUTTONS[0]);

  const currentTabState = useSelector(
    (state: RootState) => state.tabs.userManagement.tab
  );
  const kundenStep = useSelector(
    (state: RootState) => state.tabs.userManagement.kundenStep
  );
  const customerId = useSelector(
    (state: RootState) => state.user.currentUser.customerId);
  const [users, setUsers] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [user, setUser] = useState<User>({
    firstName: "",
    lastName: "",
    email: "",
    roles: [],
  });
  const [isDeleted, setIsDeleted] = useState(false);
  const scrollableWrapper = document.getElementById(
    "scrollable-wrapper"
  ) as HTMLElement;
  const [isFetching, setIsFetching] = useInfiniteScroll(
    scrollableWrapper,
    hasMore
  );
  const { isDesktop } = useScreenSize();

  const [openModal, setOpenModal] = useState(false);

  const handleChangeTab = (tab: string) => {
    setCurrentTab(tab);
    dispatch(activeTabsActions.setUserManagementTabs(tab));
    dispatch(activeTabsActions.setKundenStep(1));
  };

  const navigateToAdd = () => {
    navigate("/add-user", {
      state: {
        addSBKUser: true,
        customerAdmin: currentTab === "Kunden",
      },
    });

    setOpenModal(false);
  };

  const navigateToDeactivate = () => {
    navigate("/deactivated-users", {
      state: {
        customerAdmin: currentTab === "Kunden",
      },
    });
    setOpenModal(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let result: Technician[] = [];
      const params: UsersQueryParams = {
        "filter[roles][]": !isCustomerAdmin
          ? [ROLES.ADMIN, ROLES.TECHNICIAN]
          : [ROLES.CUSTOMER_ADMIN, ROLES.PROPERTY_MANAGER],
        "page[number]": page,
        "page[size]": 8,
        ...(isCustomerAdmin && {
          sort: 'lastName',
          'filter[customerId]': customerId,
        }),
      };

      if (!isCustomerAdmin) {
        const response = await UserService.GetAdminsAndTechnicians(params);
        result = response.data;
      } else {
        const response = await CustomerService.GetCustomerUsers(params);
        result = response.data.map((item: Technician) => ({
          ...item,
          email: item.loginName,
        }));
      }

      if (result.length === 0) {
        setHasMore(false);
      } else {
        setUsers((prevUsers) => {
          const newUsers = result.filter(
            (user) => !prevUsers.some((prevUser) => prevUser.id === user.id)
          );

          const mergedUsers = [...prevUsers, ...newUsers];

          mergedUsers.sort((a, b) =>
            (a.lastName || "").localeCompare(b.lastName || "", undefined, {
              sensitivity: "base",
            })
          );

          return mergedUsers;
        });
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackButtonClicked = () => {
    dispatch(userManagementActions.setCustomerProperties([]));
    if (kundenStep === 2) {
      dispatch(activeTabsActions.setKundenStep(1));
    } else navigate(-1);
  };
  useEffect(() => {
    if (hasMore || page === 1) fetchData();
  }, [page]);

  useEffect(() => {
    if (isFetching) {
      setPage((prevPage) => prevPage + 1);
      setIsFetching(false);
    }
  }, [isFetching]);

  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    const deleted = sessionStorage.getItem("deleted");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    if (deleted) {
      setIsDeleted(deleted === "true");
    }
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("deleted");
  }, []);
  const checkIfNeedsMore = () => {
    const wrapper = document.getElementById("scrollable-wrapper");
    if (
      wrapper &&
      wrapper.scrollHeight <= wrapper.clientHeight &&
      hasMore &&
      !loading
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };
  useEffect(() => {
    if (!isDesktop) return;
    checkIfNeedsMore();
  }, [users, hasMore, loading, isDesktop]);

  useEffect(() => {
    if (user.firstName && user.lastName && user.email && user.roles) {
      setShowNotification(true);
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [user.firstName, user.lastName, user.email, user.roles]);

  useEffect(() => {
    setCurrentTab(currentTabState);
  }, [currentTabState]);

  const shouldShowNotification =
    showNotification &&
    user.firstName &&
    user.lastName &&
    user.email &&
    user.roles;

  return (
    <AppLayout
      withBackButton={true}
      hideHeader={isCustomerAdmin}
      onGoBack={handleBackButtonClicked}
    >
      <Modal
        isOpen={openModal}
        message={t("UserManagement")}
        onConfirm={navigateToAdd}
        onCancel={navigateToDeactivate}
        cancelText={"Benutzer wiederherstellen"}
        confirmText={"neuen Benutzer erstellen"}
        backButtonShow={handleCloseModal}
        closeButtonColor="#3D5170"
        iconType={"create-sbk-user"}
      />
      <div className={styles["customer-um"]} id="scrollable-wrapper">
        <div className={styles["customer-um__heading"]}>
          <div className={styles["customer-um__back"]}>
            {isCustomerAdmin && (
              <LeftArrowIcon
                handleBackButtonClicked={handleBackButtonClicked}
              />
            )}
            <p className={styles["customer-um__title"]}>
              {t("UserManagement")}
            </p>
          </div>

          {isDesktop &&
            (currentTab !== SUB_MENU_BUTTONS[1] || kundenStep !== 1) && (
              <p>
                <label>{t("CreateUser")}</label>
                <AddUserIcon
                  width={36}
                  onClick={() => {
                    setOpenModal(true);
                  }}
                />
              </p>
            )}
        </div>

        {!isCustomerAdmin && (
          <div className={styles["tabs"]}>
            <div className={styles["buttons"]}>
              {SUB_MENU_BUTTONS.length > 0 &&
                SUB_MENU_BUTTONS.map((button, i) => (
                  <SubMenuButton
                    currentTab={currentTab}
                    value={button}
                    settings={true}
                    onSetTab={() => handleChangeTab(button)}
                    key={i}
                  />
                ))}
            </div>
          </div>
        )}

        {currentTab === SUB_MENU_BUTTONS[0] &&
          (loading && page === 1 ? (
            <LoadingSpinner loading={loading} />
          ) : (
            <>
              {shouldShowNotification && (
                <UsersCard
                  user={user}
                  color={isDeleted ? " #CED3DC" : "#e3f9fc"}
                />
              )}
              <UserListComponent users={users} loading={loading} page={page} />
            </>
          ))}
        {currentTab === SUB_MENU_BUTTONS[1] && (
          <>
            {shouldShowNotification && (
              <UsersCard
                user={user}
                color={isDeleted ? " #CED3DC" : "#e3f9fc"}
              />
            )}
            <Kunden />
          </>
        )}
      </div>
      {!isDesktop && (currentTab !== SUB_MENU_BUTTONS[1] || kundenStep !== 1) && (
        <FloatingButtons justify="right">
          <AddUserIcon
            onClick={() => {
              setOpenModal(true);
            }}
          />
        </FloatingButtons>
      )}
    </AppLayout>
  );
};
